import { Box3, Matrix4, Vector3 } from 'three';

const visibleMaterial = material => Boolean(material && material.visible !== false && material.opacity !== 0);
const rawBounds = (box, id) => ({ id, type: 'box', x0: box.min.x, x1: box.max.x,
  z0: box.min.z, z1: box.max.z, bottom: box.min.y, top: box.max.y });

export function colliderFromLocalBox(box, matrixWorld, id = box.id || 'part') {
  const local = box.isBox3 ? box.clone() : new Box3(
    new Vector3(box.x0, box.bottom, box.z0), new Vector3(box.x1, box.top, box.z1));
  if (local.isEmpty() || ![...local.min.toArray(), ...local.max.toArray()].every(Number.isFinite)) return null;
  const collider = rawBounds(local.applyMatrix4(matrixWorld), id);
  if (box.low != null) collider.low = Boolean(box.low);
  return collider;
}

function bodyBand(box, { groundHeightAt, bodyHeight, heightLimit }) {
  const ground = typeof groundHeightAt === 'function'
    ? groundHeightAt((box.min.x + box.max.x) / 2, (box.min.z + box.max.z) / 2) : NaN;
  return { bottom: Number.isFinite(ground) ? ground - 1e-6 : -Infinity,
    top: Math.min(Number.isFinite(heightLimit) ? heightLimit : Infinity,
      Number.isFinite(ground) && Number.isFinite(bodyHeight) ? ground + Math.max(0, bodyHeight) : Infinity) };
}

function clipHeight(points, height, above) {
  if (!Number.isFinite(height)) return points;
  const result = [];
  let previous = points.at(-1), previousInside = above ? previous.y >= height : previous.y <= height;
  for (const current of points) {
    const currentInside = above ? current.y >= height : current.y <= height;
    if (currentInside !== previousInside) {
      const t = (height - previous.y) / (current.y - previous.y);
      result.push(previous.clone().lerp(current, t));
    }
    if (currentInside) result.push(current);
    previous = current; previousInside = currentInside;
  }
  return result;
}

function geometryBounds(mesh, matrixWorld, options) {
  const geometry = mesh.geometry, position = geometry?.attributes?.position;
  if (!position) return null;
  geometry.computeBoundingBox();
  const sampleBounds = geometry.boundingBox.clone().applyMatrix4(matrixWorld);
  const band = bodyBand(sampleBounds, options), full = new Box3(), body = new Box3();
  if (sampleBounds.max.y < band.bottom || sampleBounds.min.y > band.top || band.bottom > band.top) return null;
  const indexed = geometry.index, count = indexed?.count ?? position.count;
  const start = Math.max(0, geometry.drawRange.start), end = Math.min(count, start + geometry.drawRange.count);
  const materialAt = offset => {
    if (!Array.isArray(mesh.material)) return mesh.material;
    const group = geometry.groups.find(group => offset >= group.start && offset + 2 < group.start + group.count);
    return group && mesh.material[group.materialIndex];
  };
  for (let offset = start; offset + 2 < end; offset += 3) {
    if (!visibleMaterial(materialAt(offset))) continue;
    const triangle = [0, 1, 2].map(index => new Vector3()
      .fromBufferAttribute(position, indexed ? indexed.getX(offset + index) : offset + index).applyMatrix4(matrixWorld));
    triangle.forEach(point => full.expandByPoint(point));
    let clipped = clipHeight(triangle, band.bottom, true);
    if (clipped.length) clipped = clipHeight(clipped, band.top, false);
    clipped.forEach(point => body.expandByPoint(point));
  }
  return full.isEmpty() || body.isEmpty() ? null : { full, body };
}

// These are unpadded physical bounds. The movement solver applies actor radius.
// Body-band clipping narrows the footprint but deliberately keeps the full top,
// so a tall trunk never becomes a low obstacle simply because its crown is high.
export function collectPhysicalColliders(root, { id = 'prop', heightLimit = Infinity,
  groundHeightAt, bodyHeight = 1.48, filter } = {}) {
  if (!root) return [];
  for (let ancestor = root; ancestor; ancestor = ancestor.parent) if (!ancestor.visible) return [];
  root.updateWorldMatrix(true, true);
  const options = { heightLimit, groundHeightAt, bodyHeight }, colliders = [];
  let meshIndex = 0;
  const visit = object => {
    if (!object.visible) return;
    if (object.isMesh && (Array.isArray(object.material) ? object.material.some(visibleMaterial) : visibleMaterial(object.material))) {
      const meshId = `${id}:${object.name || 'mesh'}:${meshIndex++}`;
      const count = object.isInstancedMesh ? object.count : 1;
      for (let index = 0; index < count; index++) {
        const matrixWorld = object.matrixWorld.clone();
        if (object.isInstancedMesh) {
          const localMatrix = new Matrix4(); object.getMatrixAt(index, localMatrix); matrixWorld.multiply(localMatrix);
        }
        if (Math.abs(matrixWorld.determinant()) < 1e-12) continue;
        const instanceIndex = object.isInstancedMesh ? index : null;
        if (filter?.(object, { instanceIndex, matrixWorld }) === false) continue;
        const instanceId = object.isInstancedMesh ? `${meshId}:instance-${index}` : meshId;
        const parts = object.userData.physicalParts;
        if (Array.isArray(parts) && parts.length) {
          parts.forEach((part, partIndex) => {
            const collider = colliderFromLocalBox(part, matrixWorld, `${instanceId}:${part.id || partIndex}`);
            if (!collider) return;
            const world = new Box3(new Vector3(collider.x0, collider.bottom, collider.z0), new Vector3(collider.x1, collider.top, collider.z1));
            const band = bodyBand(world, options);
            if (collider.top >= band.bottom && collider.bottom <= band.top && band.bottom <= band.top) colliders.push(collider);
          });
        } else {
          const bounds = geometryBounds(object, matrixWorld, options);
          if (bounds) colliders.push({ ...rawBounds(bounds.body, instanceId), bottom: bounds.full.min.y, top: bounds.full.max.y });
        }
      }
    }
    object.children.forEach(visit);
  };
  visit(root);
  return colliders;
}
